import React, { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { User } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useProfile, profileKeys } from '../../hooks/useProfile';
import TaskModal, { Task } from '../modals/TaskModal/TaskModal';
import { tasksService } from '../../services/tasksService';
import { userService } from '../../services/userService';
import { calendarService } from '../../services/calendarService';
import { CALENDAR_EVENT_TYPES, getCalendarEventTypeDef } from '../../constants/calendarEventTypes';
import {
  pad2,
  monthGridVisibleRangeIso,
  calendarEventsQueryKey,
  hasProfilePermission,
  formatEventClockInCrmTz,
  crmCalendarWallDate,
  isWorkplaceCalendarEventPastByMoscowCalendarDay,
} from '../../utils/calendarShared';
import CustomScrollbar from '../common/CustomScrollbar';
import './Workplace.css';

interface WorkplaceProps {
  user: User | null;
}

type SortDirection = 'asc' | 'desc' | null;

type WorkplaceTask = {
  id: number;
  name: string;
  client: string;
  clientId?: number;
  project: string;
  projectId?: number;
  case: string;
  caseId?: number;
  dueDate: Date | null;
  dueTime: string;
  createdAt: string;
  executor: string;
  assigner: string;
};

const Workplace: React.FC<WorkplaceProps> = ({ user }) => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { data: profile } = useProfile();
  const hasTasksSection = useMemo(
    () => hasProfilePermission(profile, 'global_access_work_tasks'),
    [profile],
  );
  const [selectedCategory, setSelectedCategory] = useState<string>('Все');
  const [selectedTaskFilter, setSelectedTaskFilter] = useState<string>('Все');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  // Используем аватарку переданного пользователя, если она есть, иначе из профиля или дефолтную
  const avatar = user?.avatar || profile?.avatar || '/avatar.png';

  const {
    data: tasksList,
    isLoading: tasksLoading,
    isError: tasksError,
  } = useQuery({
    queryKey: ['tasks', 'workplace', user?.id ?? 0],
    queryFn: () =>
      tasksService.getTasks({ per_page: 100, dashboard_for_user_id: Number(user!.id) }),
    enabled: !!user && Number.isFinite(Number(user.id)),
    staleTime: 60 * 1000,
  });

  const allTasks: WorkplaceTask[] = useMemo(() => {
    return (tasksList?.data ?? []).map((t) => {
        const parseDeadlineFormatted = (s: string): Date | null => {
          const str = (s || '').trim();
          const [datePart, timePart] = str.split(/\s+/);
          const [d, m, y] = (datePart || '').split('.').map(Number);
          const [hh = 0, mm = 0] = (timePart || '00:00').split(':').map(Number);
          if (!d || !m || !y) return null;
          const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
          return Number.isNaN(dt.getTime()) ? null : dt;
        };

        // В дашборде показываем "как у пользователя", поэтому предпочитаем `deadline_formatted` (без UTC-сдвига),
        // а ISO `deadline` используем как fallback.
        const deadline =
          (t.deadline_formatted ? parseDeadlineFormatted(t.deadline_formatted) : null) ??
          (t.deadline ? new Date(t.deadline) : null);

        const dueTime =
          t.deadline_formatted?.split(/\s+/)[1] ||
          (deadline
            ? `${String(deadline.getHours()).padStart(2, '0')}:${String(deadline.getMinutes()).padStart(2, '0')}`
            : '');
        return {
          id: t.id,
          name: t.title,
          client: t.client?.name ?? '—',
          clientId: t.client_id ?? undefined,
          project: t.project?.name ?? '',
          projectId: t.project_id ?? undefined,
          case: t.case?.name ?? '—',
          caseId: t.case_id ?? undefined,
          dueDate: deadline,
          dueTime,
          createdAt: t.created_at_formatted ?? '',
          executor: t.performers?.[0]?.name ?? '',
          assigner: t.assigner?.name ?? '',
        };
      });
  }, [tasksList]);

  // Функция форматирования даты
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Функция фильтрации задач
  const filterTasks = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
    
    const endOfMonth = new Date(today);
    endOfMonth.setMonth(today.getMonth() + 1);
    endOfMonth.setDate(0);

    switch (selectedTaskFilter) {
      case 'Сегодня':
        return allTasks.filter((task) => {
          if (!task.dueDate) return false;
          const taskDate = new Date(task.dueDate);
          taskDate.setHours(0, 0, 0, 0);
          return taskDate.getTime() === today.getTime();
        });
      case 'На неделю':
        return allTasks.filter((task) => {
          if (!task.dueDate) return false;
          const taskDate = new Date(task.dueDate);
          taskDate.setHours(0, 0, 0, 0);
          return taskDate >= today && taskDate <= endOfWeek;
        });
      case 'На месяц':
        return allTasks.filter((task) => {
          if (!task.dueDate) return false;
          const taskDate = new Date(task.dueDate);
          taskDate.setHours(0, 0, 0, 0);
          return taskDate >= today && taskDate <= endOfMonth;
        });
      case 'Все':
      default:
        return allTasks;
    }
  };

  // Функция сортировки задач по срокам выполнения
  const sortTasksByDueDate = (tasks: WorkplaceTask[], direction: SortDirection) => {
    if (!direction) return tasks;
    
    const sorted = [...tasks].sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      const dateA = new Date(a.dueDate);
      const dateB = new Date(b.dueDate);
      
      // Учитываем также время
      const timeA = a.dueTime.split(':').map(Number);
      const timeB = b.dueTime.split(':').map(Number);
      dateA.setHours(timeA[0], timeA[1]);
      dateB.setHours(timeB[0], timeB[1]);
      
      if (direction === 'asc') {
        return dateA.getTime() - dateB.getTime();
      } else {
        return dateB.getTime() - dateA.getTime();
      }
    });
    
    return sorted;
  };

  const handleSortByDueDate = () => {
    if (sortDirection === null) {
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortDirection(null);
    }
  };

  const filteredTasks = filterTasks();
  const tasks = sortDirection ? sortTasksByDueDate(filteredTasks, sortDirection) : filteredTasks;

  const tasksEmptyMessage =
    allTasks.length === 0
      ? 'Нет активных задач в работе.'
      : 'Нет задач в выбранном периоде';

  const canViewStaffCalendars = useMemo(
    () => hasProfilePermission(profile, 'calendar_view_all'),
    [profile]
  );

  const calendarCategories = useMemo(() => ['Все', ...CALENDAR_EVENT_TYPES.map((t) => t.name)], []);

  const { from: rangeFrom, to: rangeTo } = monthGridVisibleRangeIso(new Date());

  /** Расписание владельца дашборда: свой — как на /calendar без user_ids; чужой — по user_ids при праве calendar_view_all. */
  const isDashboardForSelf = !!(user && authUser && user.id === authUser.id);
  const canLoadCalendarForSelectedDashboard = isDashboardForSelf || canViewStaffCalendars;

  const workplaceCalendarExtraKey = useMemo(() => {
    if (!user) return 'none';
    if (isDashboardForSelf) return 'me';
    return canViewStaffCalendars ? `user-${user.id}` : 'forbidden';
  }, [user, isDashboardForSelf, canViewStaffCalendars]);

  const workplaceCalendarUserIds = useMemo(() => {
    if (!user || isDashboardForSelf) return undefined;
    if (canViewStaffCalendars) return [user.id];
    return undefined;
  }, [user, isDashboardForSelf, canViewStaffCalendars]);

  /** Сайдбар: is_online из /users/dashboard; свой стол через шапку — в auth нет поля, дублируем из /profile */
  const showOnlineIndicator = Boolean(
    user && (user.is_online === true || (isDashboardForSelf && profile?.is_online === true)),
  );

  const { data: workplaceStats, isLoading: workplaceStatsLoading } = useQuery({
    queryKey: profileKeys.workplaceStats(Number(user?.id ?? 0)),
    queryFn: () => userService.getStatistics({ for_user_id: Number(user!.id) }),
    enabled: !!user && Number.isFinite(Number(user.id)),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  const statNum = (n: number | undefined) =>
    workplaceStatsLoading ? '…' : (n ?? 0).toLocaleString('ru-RU');

  const { data: calendarApiEvents = [], isLoading: calendarEventsLoading, isError: calendarEventsError } = useQuery({
    queryKey: calendarEventsQueryKey(rangeFrom, rangeTo, workplaceCalendarExtraKey),
    queryFn: () => calendarService.list(rangeFrom, rangeTo, workplaceCalendarUserIds),
    enabled: !!user && canLoadCalendarForSelectedDashboard,
    refetchOnMount: true,
  });

  const dashboardCalendarEvents = useMemo(() => {
    if (!canLoadCalendarForSelectedDashboard) return [];
    return calendarApiEvents
      .filter((row) => !isWorkplaceCalendarEventPastByMoscowCalendarDay(row))
      .map((row) => {
        const start = new Date(row.start_at);
        const def = getCalendarEventTypeDef(row.event_type);
        const wall = crmCalendarWallDate(row.start_at);
        const dateStr = row.all_day
          ? `${pad2(wall.getDate())}.${pad2(wall.getMonth() + 1)}.${wall.getFullYear()}`
          : `${pad2(wall.getDate())}.${pad2(wall.getMonth() + 1)}.${wall.getFullYear()} - ${formatEventClockInCrmTz(row.start_at)}`;
        let text = row.title;
        const projectName = row.project?.name ?? row.project_label;
        const caseName = row.case?.name ?? row.case_label;
        if (projectName) {
          text += ` · ${projectName}`;
        } else if (caseName) {
          text += ` · ${caseName}`;
        }
        return {
          id: row.id,
          icon: def.icon,
          text,
          date: dateStr,
          category: def.name,
          color: def.color,
          sortKey: start.getTime(),
        };
      })
      .sort((a, b) => a.sortKey - b.sortKey);
  }, [calendarApiEvents, canLoadCalendarForSelectedDashboard]);

  const filteredEvents =
    selectedCategory === 'Все'
      ? dashboardCalendarEvents
      : dashboardCalendarEvents.filter((event) => event.category === selectedCategory);

  const goToCalendarEvent = useCallback(
    (eventId: number) => {
      if (!user) return;
      navigate('/calendar', {
        state: {
          openEventId: eventId,
          ...(!isDashboardForSelf && canViewStaffCalendars
            ? { calendarStaffUserId: user.id, calendarStaffUserName: user.name }
            : {}),
        },
      });
    },
    [user, navigate, isDashboardForSelf, canViewStaffCalendars],
  );

  if (!user) {
    return (
      <div className="workplace">
        <div className="workplace-empty">
          <p>Не удалось загрузить данные пользователя</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workplace">
      <div className="workplace-header">
        <h1 className="workplace-title">
          <img src="/icons/workplace/rabstol.svg" alt="Рабочий стол" className="workplace-title-icon" />
          Рабочий стол
        </h1>
      </div>
      
      <div className="workplace-content">
        {/* Верхняя строка: информация о пользователе и календарь */}
        <div className="workplace-top-row">
          {/* Левая часть: информация о пользователе и статистика в одном контейнере */}
          <div className="user-profile-container">
            <div className="user-profile-section">
              <div className="profile-avatar-wrapper">
                <img
                  src={avatar}
                  alt={user.name}
                  className="profile-avatar"
                />
                {showOnlineIndicator && <span className="profile-online-indicator"></span>}
              </div>
              <div className="profile-info">
                <h2 className="profile-name">{user.name}</h2>
                {(user.positionTitle || (isDashboardForSelf ? profile?.position_data?.name : undefined)) && (
                  <p className="profile-position">
                    {user.positionTitle || (isDashboardForSelf ? profile?.position_data?.name : undefined)}
                  </p>
                )}
                {user.last_activity && (
                  <p className="profile-activity">
                    Последняя активность {user.last_activity}
                  </p>
                )}
              </div>
            </div>

            <div className="workplace-stats">
              <h3 className="stats-title">Статистика профиля</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{statNum(workplaceStats?.projects_participation)}</div>
                  <div className="stat-label">Участие в проектах</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{statNum(workplaceStats?.tasks_in_progress)}</div>
                  <div className="stat-label">Задач сейчас в работе</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{statNum(workplaceStats?.high_priority_tasks)}</div>
                  <div className="stat-label">Задач с высоким приоритетом</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{statNum(workplaceStats?.overdue_tasks)}</div>
                  <div className="stat-label">Задач с истекшим сроком</div>
                </div>
              </div>
            </div>
          </div>

          {/* Правая часть: календарь */}
          <div className="calendar-container">
            <div className="calendar-section">
              <div className="calendar-header-row">
                <h3 className="calendar-header">
                  <img src="/icons/navigation/calendar.svg" alt="" className="calendar-header-icon" />
                  Календарь
                </h3>
                <Link
                  to="/calendar"
                  state={
                    !isDashboardForSelf && canViewStaffCalendars
                      ? { calendarStaffUserId: user.id, calendarStaffUserName: user.name }
                      : undefined
                  }
                  className="workplace-calendar-open-link"
                >
                  Открыть календарь
                </Link>
              </div>

              <div className="calendar-filters">
                {calendarCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={`calendar-filter-btn ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>

              <CustomScrollbar
                trackRight={0}
                trackTop={0}
                trackClassName="calendar-scrollbar-track"
              >
                <div className="calendar-events">
                  {calendarEventsLoading ? (
                    <div className="calendar-events-loading">
                      <div className="workplace-calendar-inline-spinner" aria-hidden />
                      <p>Загрузка событий…</p>
                    </div>
                  ) : calendarEventsError ? (
                    <div className="calendar-no-events">
                      <p>Не удалось загрузить события</p>
                    </div>
                  ) : filteredEvents.length > 0 ? (
                    filteredEvents.map((event) => (
                      <div
                        key={event.id}
                        role="button"
                        tabIndex={0}
                        className="calendar-event-item"
                        style={{ borderLeftColor: event.color }}
                        onClick={() => goToCalendarEvent(event.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            goToCalendarEvent(event.id);
                          }
                        }}
                        aria-label={`Открыть в календаре: ${event.text}`}
                      >
                        <span className="event-icon">{event.icon}</span>
                        <span className="event-text">{event.text}</span>
                        <span className="event-date">{event.date}</span>
                      </div>
                    ))
                  ) : (
                    <div className="calendar-no-events">
                      <p>Нет событий в этой категории</p>
                    </div>
                  )}
                </div>
              </CustomScrollbar>
            </div>
          </div>
        </div>

        {/* Нижняя часть: задачи */}
        <div className="tasks-container">
          <div className="tasks-section">
            <div className="tasks-header-row">
              <div className="tasks-header-group">
                <h3 className="tasks-header">Мои задачи</h3>
                <div className="tasks-filters">
                  {['Все', 'Сегодня', 'На неделю', 'На месяц'].map((filter) => (
                    <button
                      key={filter}
                      className={`tasks-filter-btn ${selectedTaskFilter === filter ? 'active' : ''}`}
                      onClick={() => setSelectedTaskFilter(filter)}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
              
              {hasTasksSection && (
                <Link to="/tasks/new" className="create-task-btn" role="button">
                  <img src="/icons/clients/plus-circle.svg" alt="Создать" className="create-task-icon" />
                  Создать задачу
                </Link>
              )}
            </div>
            
            <CustomScrollbar 
              trackRight={0}
              trackTop={0}
              trackClassName="tasks-scrollbar-track"
            >
              <div className="tasks-table-wrapper">
                <table className="tasks-table">
                <thead>
                  <tr>
                    <th>Название задачи</th>
                    <th>Клиент</th>
                    <th>Проект</th>
                    <th>Дело</th>
                    <th 
                      className={`sortable ${sortDirection === 'asc' ? 'sort-asc' : sortDirection === 'desc' ? 'sort-desc' : ''}`}
                      onClick={handleSortByDueDate}
                    >
                      Сроки выполнения до
                    </th>
                    <th>Постановщик</th>
                  </tr>
                </thead>
                <tbody>
                  {tasksLoading ? (
                    <tr>
                      <td colSpan={6} className="tasks-no-results">
                        <p>Загрузка задач…</p>
                      </td>
                    </tr>
                  ) : tasksError ? (
                    <tr>
                      <td colSpan={6} className="tasks-no-results">
                        <p>Не удалось загрузить задачи. Проверьте сеть и что на сервере выполнена миграция БД.</p>
                      </td>
                    </tr>
                  ) : tasks.length > 0 ? (
                    tasks.map((task, index) => {
                      const formattedDueDate = task.dueDate
                        ? `${formatDate(task.dueDate)} ${task.dueTime}`.trim()
                        : '';
                      const taskForModal: Task = {
                        id: task.id,
                        name: task.name,
                        assignmentDate: task.createdAt || '—',
                        dueDate: formattedDueDate,
                        assigner: task.assigner,
                        executor: task.executor,
                      };
                      
                      return (
                        <tr 
                          key={task.id} 
                          className={index % 2 === 0 ? 'even' : 'odd'}
                          onClick={() => {
                            setSelectedTask(taskForModal);
                            setIsTaskModalOpen(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <td data-label="Название задачи">{task.name}</td>
                          <td data-label="Клиент">{task.client}</td>
                          <td data-label="Проект">{task.project}</td>
                          <td data-label="Дело">{task.case}</td>
                          <td data-label="Сроки выполнения до">
                            <div className="task-due-date">
                              <img src="/icons/workplace/date.svg" alt="Дата" className="date-icon" />
                              <span>{task.dueDate ? formatDate(task.dueDate) : '—'}</span>
                              <img src="/icons/workplace/time.svg" alt="Время" className="time-icon" />
                              <span>{task.dueTime || '—'}</span>
                            </div>
                          </td>
                          <td data-label="Постановщик">{task.assigner}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="tasks-no-results">
                        <p>{tasksEmptyMessage}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </CustomScrollbar>
          </div>
        </div>
      </div>

      {/* Модальное окно задачи */}
      {selectedTask && (
        <TaskModal
          isOpen={isTaskModalOpen}
          onClose={() => {
            setIsTaskModalOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          projectName={tasks.find((t) => t.id === selectedTask.id)?.project || ''}
          projectId={tasks.find((t) => t.id === selectedTask.id)?.projectId}
          clientName={tasks.find((t) => t.id === selectedTask.id)?.client || ''}
          clientId={tasks.find((t) => t.id === selectedTask.id)?.clientId}
          caseName={(() => {
            const task = tasks.find((t) => t.id === selectedTask.id);
            return task && task.case && task.case !== '—' ? task.case : undefined;
          })()}
          caseId={tasks.find((t) => t.id === selectedTask.id)?.caseId}
        />
      )}
    </div>
  );
};

export default Workplace;

